import { useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import type { Card, Color, LandCategory } from '@mtg-deck-tool/shared';
import { getCardImageUri, isLand } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../../stores/deck-store.js';
import { useLandPrefsStore } from '../../stores/land-prefs-store.js';
import { useLandProposalStore, type ResolvedProposalEntry } from '../../stores/land-proposal-store.js';
import { useLandAutofill } from '../../hooks/useLandAutofill.js';
import { api } from '../../lib/api-client.js';
import { LAND_CATALOG } from '../../data/land-catalog.js';
import LandCountInput from './LandCountInput.js';
import LandTypeSelector from './LandTypeSelector.js';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LandAutofillModalProps {
  onClose: () => void;
}

type ModalStep = 'utility' | 'configure';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Two-stage modal for generating a land base (rev 4 — Karsten-based, instant generation).
 *
 * Step 1 (utility): Review utility lands already in the deck.
 * Step 2 (configure): Choose target count, categories, then Generate.
 * Stage 'review': auto-resolves cards, calls setPendingProposal, then closes.
 *   Generation is synchronous — no 'generating' intermediate stage.
 *
 * Rendered via ReactDOM.createPortal into document.body.
 */
export default function LandAutofillModal({ onClose }: LandAutofillModalProps) {
  const entries = useDeckStore((s) => s.entries);
  const colorIdentity = useDeckStore((s) => s.colorIdentity);
  const enabledCategories = useLandPrefsStore((s) => s.enabledCategories);

  const setPendingProposal = useLandProposalStore((s) => s.setPendingProposal);

  const {
    stage,
    targetCount,
    setTargetCount,
    mode,
    setMode,
    allocations,
    toggleCategory,
    hint,
    existingUtilityLandNames,
    proposal,
    startGeneration,
    generationError,
    panelData,
  } = useLandAutofill();

  // ── Local modal step (utility → configure, inside configure stage) ──
  const hasUtilityLands = existingUtilityLandNames.length > 0;
  const [step, setStep] = useState<ModalStep>(hasUtilityLands ? 'utility' : 'configure');

  // Which utility lands to keep
  const [keptUtilityLands, setKeptUtilityLands] = useState<Set<string>>(
    () => new Set(existingUtilityLandNames),
  );

  function toggleUtilityLand(name: string) {
    setKeptUtilityLands((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  // Staple/rainbow lands — persisted in land-prefs-store
  const savedStaples = useLandPrefsStore((s) => s.selectedStaples);
  const setSavedStaples = useLandPrefsStore((s) => s.setSelectedStaples);

  const [selectedStaples, setSelectedStaplesLocal] = useState<Set<string>>(() => {
    // Use persisted selection if available, otherwise default to all eligible rainbow lands
    if (savedStaples.length > 0) {
      return new Set(savedStaples);
    }
    const rainbowCycle = LAND_CATALOG.find((c) => c.category === 'rainbow');
    if (!rainbowCycle) return new Set<string>();
    return new Set(
      rainbowCycle.entries
        .filter((e) => e.produces.length === 0 || e.produces.every((c) => new Set(colorIdentity).has(c)))
        .map((e) => e.name),
    );
  });

  // Wrapper that updates both local state and persisted store
  function setSelectedStaples(updater: Set<string> | ((prev: Set<string>) => Set<string>)) {
    setSelectedStaplesLocal((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setSavedStaples(Array.from(next));
      return next;
    });
  }

  const [showStaples, setShowStaples] = useState(false);

  const stapleLands = useMemo(() => {
    const cycle = LAND_CATALOG.find((c) => c.category === 'rainbow');
    if (!cycle) return [];
    return cycle.entries.filter((e) =>
      e.produces.length === 0 || e.produces.every((c) => new Set(colorIdentity).has(c)),
    );
  }, [colorIdentity]);

  function toggleStaple(name: string) {
    setSelectedStaples((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  // Minimum basics per color
  const [minBasics, setMinBasics] = useState<Record<Color, number>>(() => ({
    W: colorIdentity.includes('W') ? 1 : 0,
    U: colorIdentity.includes('U') ? 1 : 0,
    B: colorIdentity.includes('B') ? 1 : 0,
    R: colorIdentity.includes('R') ? 1 : 0,
    G: colorIdentity.includes('G') ? 1 : 0,
  }));

  function handleMinBasicChange(color: Color, value: number) {
    setMinBasics((prev) => ({ ...prev, [color]: Math.max(0, value) }));
  }

  // Proposal resolve state (auto-triggered when stage reaches 'review')
  const [isResolvingProposal, setIsResolvingProposal] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Build a lookup from name → Card for utility lands (for images)
  const utilityLandCards = useMemo(() => {
    const map = new Map<string, Card>();
    for (const entry of entries) {
      if (entry.isCommander) continue;
      if (!isLand(entry.card)) continue;
      if (existingUtilityLandNames.includes(entry.card.name)) {
        map.set(entry.card.name, entry.card);
      }
    }
    return map;
  }, [entries, existingUtilityLandNames]);

  const overlayRef = useRef<HTMLDivElement>(null);

  // ── Keyboard: Escape to close ──
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Click outside to close ──
  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target !== overlayRef.current) return;
    onClose();
  }

  // Category constraints state (min/max per category)
  const [categoryConstraints, setCategoryConstraints] = useState<
    Map<LandCategory, { min?: number; max?: number }>
  >(new Map());

  function setCategoryMin(category: LandCategory, value: number | undefined) {
    setCategoryConstraints((prev) => {
      const next = new Map(prev);
      const existing = next.get(category) ?? {};
      if (value === undefined || value <= 0) {
        const { min: _min, ...rest } = existing as { min?: number; max?: number };
        void _min;
        if (Object.keys(rest).length === 0) {
          next.delete(category);
        } else {
          next.set(category, rest);
        }
      } else {
        next.set(category, { ...existing, min: value });
      }
      return next;
    });
  }

  function setCategoryMax(category: LandCategory, value: number | undefined) {
    setCategoryConstraints((prev) => {
      const next = new Map(prev);
      const existing = next.get(category) ?? {};
      if (value === undefined || value <= 0) {
        const { max: _max, ...rest } = existing as { min?: number; max?: number };
        void _max;
        if (Object.keys(rest).length === 0) {
          next.delete(category);
        } else {
          next.set(category, rest);
        }
      } else {
        next.set(category, { ...existing, max: value });
      }
      return next;
    });
  }

  // ── Generate handler ──
  function handleGenerate() {
    startGeneration({
      targetCount,
      enabledCategories: enabledCategories as LandCategory[],
      mode,
      stapleNames: selectedStaples,
      keptUtilityNames: keptUtilityLands,
      minBasicsPerColor: minBasics,
      ...(categoryConstraints.size > 0 ? { categoryConstraints } : {}),
    });
  }

  // ── Auto-transition: when generation finishes, resolve cards and push to proposal store ──
  useEffect(() => {
    if (stage !== 'review' || isResolvingProposal || proposal.length === 0) return;

    let cancelled = false;
    setIsResolvingProposal(true);
    setResolveError(null);

    const uniqueNames = Array.from(new Set(proposal.map((p) => p.name)));
    api.resolveCards(uniqueNames).then(({ found }) => {
      if (cancelled) return;

      // Build a name → Card lookup
      const cardByName = new Map<string, Card>();
      for (const card of found) {
        cardByName.set(card.name, card);
      }

      // Build catalog name → category lookup for category assignment
      const nameToCat = new Map<string, LandCategory>();
      for (const cycle of LAND_CATALOG) {
        for (const entry of cycle.entries) {
          if (!nameToCat.has(entry.name)) nameToCat.set(entry.name, cycle.category);
        }
      }

      const resolvedEntries: ResolvedProposalEntry[] = [];
      for (const proposed of proposal) {
        const card = cardByName.get(proposed.name);
        if (!card) continue;
        resolvedEntries.push({
          card,
          quantity: proposed.quantity,
          category: proposed.category,
          alreadyInDeck: proposed.alreadyInDeck,
        });
      }

      setPendingProposal({
        rawProposal: proposal,
        resolvedEntries,
        mode: mode as 'generate' | 'complete',
        deckColorIdentity: colorIdentity as Color[],
        ...(panelData?.beforeSnapshot ? { beforeSnapshot: panelData.beforeSnapshot } : {}),
        karstenRows: panelData?.karstenRows ?? [],
        manaOverviewRows: panelData?.manaOverviewRows ?? [],
        ...(panelData?.landDiff ? { landDiff: panelData.landDiff } : {}),
      });

      onClose();
    }).catch((err) => {
      if (cancelled) return;
      setResolveError(err instanceof Error ? err.message : 'Failed to resolve cards');
      setIsResolvingProposal(false);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ── Derived state for footer summary ──
  const stapleCount = selectedStaples.size;
  const keptUtility = keptUtilityLands.size;
  const minBasicFloor = Object.values(minBasics).reduce((s, v) => s + v, 0);
  const derivedBasics = minBasicFloor;
  const multicolorTotal = stapleCount;
  const actualTotal = multicolorTotal + keptUtility + derivedBasics;
  const isOverTarget = actualTotal > targetCount;

  // ── Render ──
  const modalContent = (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Generate Land Base"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-100">
            {step === 'utility' && stage === 'configure' ? 'Utility Lands in Your Deck' : 'Generate Land Base'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors text-xl leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* ── Review stage: auto-resolves cards and pushes to proposal store, then closes ── */}
          {stage === 'review' && (
            <div className="flex flex-col items-center gap-4 py-8">
              {resolveError ? (
                <>
                  <p className="text-red-400 text-sm">{resolveError}</p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-gray-400 text-sm">Preparing land base…</p>
                </>
              )}
            </div>
          )}

          {/* ── Configure stage (step: utility or configure) ── */}
          {stage === 'configure' && (
            <>
              {step === 'utility' && (
                <>
                  <p className="text-sm text-gray-400">
                    These utility lands are already in your deck. Keep the ones you want — removed
                    lands free up slots for the generated manabase.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {existingUtilityLandNames.map((name) => {
                      const card = utilityLandCards.get(name);
                      const imageUri = card ? getCardImageUri(card, 'normal') : undefined;
                      const isKept = keptUtilityLands.has(name);

                      return (
                        <div
                          key={name}
                          className={`relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                            isKept
                              ? 'border-orange-500 opacity-100'
                              : 'border-gray-700 opacity-40 grayscale'
                          }`}
                          onClick={() => toggleUtilityLand(name)}
                        >
                          {imageUri ? (
                            <img
                              src={imageUri}
                              alt={name}
                              className="w-full aspect-[5/7] object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full aspect-[5/7] bg-gray-800 flex items-center justify-center">
                              <span className="text-xs text-gray-500 text-center px-2">{name}</span>
                            </div>
                          )}
                          <div className={`absolute top-1.5 right-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            isKept
                              ? 'bg-green-700/90 text-green-200'
                              : 'bg-red-900/90 text-red-300'
                          }`}>
                            {isKept ? 'Keep' : 'Remove'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {step === 'configure' && (
                <>
                  {/* Land count + basic minimums */}
                  <div className="flex gap-6 items-start">
                    <LandCountInput
                      value={targetCount}
                      onChange={setTargetCount}
                      hint={hint}
                    />

                    <div className="shrink-0">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Minimum basics
                      </label>
                      <div className="flex gap-3">
                        {colorIdentity.map((color) => {
                          const name = ({ W: 'Plains', U: 'Island', B: 'Swamp', R: 'Mountain', G: 'Forest' } as Record<Color, string>)[color];
                          const value = minBasics[color];
                          return (
                            <div key={color} className="flex flex-col items-center gap-1">
                              <span className="text-[10px] text-gray-400">{name}</span>
                              <div className="flex items-center gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => handleMinBasicChange(color, value - 1)}
                                  disabled={value <= 0}
                                  className={`w-5 h-5 rounded text-xs font-bold transition-colors ${
                                    value > 0
                                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                      : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                  }`}
                                >
                                  −
                                </button>
                                <span className="text-xs text-gray-200 w-4 text-center tabular-nums">{value}</span>
                                <button
                                  type="button"
                                  onClick={() => handleMinBasicChange(color, value + 1)}
                                  className="w-5 h-5 rounded text-xs font-bold bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-gray-500 text-[10px] mt-1">
                        Per color — rest by simulation
                      </p>
                    </div>
                  </div>

                  {/* Staple lands */}
                  {stapleLands.length > 0 && (
                    <div className="border border-gray-700 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowStaples(!showStaples)}
                        className="flex items-center justify-between w-full px-3 py-2 bg-gray-800/50 hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] transition-transform ${showStaples ? 'rotate-90' : ''}`}>▶</span>
                          <span className="text-xs font-medium text-gray-200">Staple Lands</span>
                          <span className="text-[10px] text-gray-500">{selectedStaples.size} selected</span>
                        </div>
                      </button>
                      {showStaples && (
                        <div className="px-3 py-2 space-y-1 border-t border-gray-700/50">
                          {stapleLands.map((entry) => {
                            const isSelected = selectedStaples.has(entry.name);
                            return (
                              <label key={entry.name} className="flex items-center gap-2 cursor-pointer py-0.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleStaple(entry.name)}
                                  className="accent-orange-500"
                                />
                                <span className={`text-xs ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                                  {entry.name}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Category distribution */}
                  <LandTypeSelector
                    allocations={allocations}
                    enabledCategories={enabledCategories}
                    onToggle={toggleCategory}
                    categoryConstraints={categoryConstraints}
                    onSetMin={setCategoryMin}
                    onSetMax={setCategoryMax}
                  />

                  {/* Generation error */}
                  {generationError && (
                    <p className="text-red-400 text-sm">{generationError}</p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-700 shrink-0 space-y-3">
          {/* Summary line (configure stage only) */}
          {stage === 'configure' && step === 'configure' && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="text-gray-400">
                  Multicolor: <span className="text-gray-200 font-medium">{multicolorTotal}</span>
                </span>
                {keptUtility > 0 && (
                  <span className="text-gray-400">
                    Utility: <span className="text-gray-200 font-medium">{keptUtility}</span>
                  </span>
                )}
                <span className="text-gray-400">
                  Basics: <span className="text-gray-200 font-medium">{derivedBasics}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isOverTarget && (
                  <span className="text-amber-400 text-xs font-medium">
                    {actualTotal - targetCount} over target
                  </span>
                )}
                <span className={isOverTarget ? 'text-amber-400' : 'text-gray-400'}>
                  Total: <span className={`font-medium ${isOverTarget ? 'text-amber-300' : 'text-gray-200'}`}>{actualTotal}</span>
                  {isOverTarget && <span className="text-gray-500 ml-1">/ {targetCount}</span>}
                </span>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 justify-end">
            {/* Review: auto-closing — no manual buttons needed (resolving spinner shown in content) */}

            {/* Configure: utility step or configure step */}
            {stage === 'configure' && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>

                {step === 'utility' && (
                  <button
                    type="button"
                    onClick={() => setStep('configure')}
                    className="px-5 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors"
                  >
                    Next
                  </button>
                )}

                {step === 'configure' && (
                  <>
                    {hasUtilityLands && (
                      <button
                        type="button"
                        onClick={() => setStep('utility')}
                        className="px-4 py-2 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={targetCount <= 0}
                      className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${
                        targetCount > 0
                          ? 'bg-orange-600 hover:bg-orange-500 text-white'
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Generate
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
